import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Tag } from 'primeng/tag';
import { Textarea } from 'primeng/textarea';
import { DisplayInfo, GameVariantTemplate } from '@/lib-backend';
import { RealmGameVariantsService } from '@/app/services/realm-game-variants.service';
import {
    modeConfigTypeLabel,
    variantEntryFee,
    variantPlayerCount,
    variantTopReward,
} from '@/app/utils/variant';
import { resolveGameContentUrl } from '@/app/utils/game-content-url';
import { FundsDisplay } from '@/app/components/funds-display';

interface ConfigureDraft {
    overrideDisplayInfo: boolean;
    displayName: string;
    displayDescription: string;
    displayImageUrl: string;
    overrideIsActive: boolean;
    isActive: boolean;
}

const EMPTY_DRAFT: ConfigureDraft = {
    overrideDisplayInfo: false,
    displayName: '',
    displayDescription: '',
    displayImageUrl: '',
    overrideIsActive: false,
    isActive: true,
};

@Component({
    selector: 'app-configure-variant-dialog',
    standalone: true,
    imports: [FormsModule, Button, Checkbox, Dialog, InputText, Message, Tag, Textarea, FundsDisplay],
    templateUrl: './configure-variant-dialog.html',
})
export class ConfigureVariantDialog {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }
    @Input({ required: true }) realmId = '';
    @Input({ required: true }) gameId = '';

    @Input() set template(t: GameVariantTemplate | null) {
        this.templateSignal.set(t);
    }

    @Input() set gameTitleOverride(v: string | null) {
        this.gameTitleOverrideSignal.set(v);
    }

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() created = new EventEmitter<void>();

    protected readonly variantsService = inject(RealmGameVariantsService);

    protected readonly visibleSignal = signal(false);
    protected readonly templateSignal = signal<GameVariantTemplate | null>(null);
    protected readonly gameTitleOverrideSignal = signal<string | null>(null);
    protected readonly draft = signal<ConfigureDraft>({ ...EMPTY_DRAFT });

    protected readonly saving = signal(false);
    protected readonly saveError = signal<string | null>(null);

    protected readonly canSave = computed(() => {
        const d = this.draft();
        if (!this.templateSignal()) return false;
        if (d.overrideDisplayInfo && !d.displayName.trim()) return false;
        return true;
    });

    protected readonly modeConfigTypeLabel = modeConfigTypeLabel;
    protected readonly variantPlayerCount = variantPlayerCount;
    protected readonly variantEntryFee = variantEntryFee;
    protected readonly variantTopReward = variantTopReward;
    protected readonly resolveGameContentUrl = resolveGameContentUrl;

    constructor() {
        effect(() => {
            if (this.visibleSignal()) {
                const t = this.templateSignal();
                if (t) {
                    const titleOverride = this.gameTitleOverrideSignal();
                    const hasGameTitleOverride = titleOverride !== null && titleOverride.trim() !== '';
                    this.draft.set({
                        ...EMPTY_DRAFT,
                        overrideDisplayInfo: hasGameTitleOverride,
                        displayName: hasGameTitleOverride ? titleOverride! : (t.DisplayInfo.Name ?? ''),
                        displayDescription: t.DisplayInfo.Description ?? '',
                        displayImageUrl: t.DisplayInfo.ImageUrl ?? '',
                    });
                    this.saveError.set(null);
                }
            }
        });
    }

    protected setField<K extends keyof ConfigureDraft>(key: K, value: ConfigureDraft[K]): void {
        this.draft.update((d) => ({ ...d, [key]: value }));
    }

    protected previewName(t: GameVariantTemplate): string {
        const d = this.draft();
        return d.overrideDisplayInfo ? d.displayName : t.DisplayInfo.Name;
    }

    protected previewDescription(t: GameVariantTemplate): string {
        const d = this.draft();
        return d.overrideDisplayInfo ? d.displayDescription : (t.DisplayInfo.Description ?? '');
    }

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
        if (!value) {
            this.draft.set({ ...EMPTY_DRAFT });
            this.saveError.set(null);
        }
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    protected async save(): Promise<void> {
        const t = this.templateSignal();
        if (!this.canSave() || !t) return;
        const d = this.draft();
        const displayInfo: DisplayInfo | null = d.overrideDisplayInfo
            ? {
                Name: d.displayName,
                Description: d.displayDescription,
                ImageUrl: d.displayImageUrl,
            }
            : null;
        const isActive = d.overrideIsActive ? d.isActive : null;

        this.saving.set(true);
        this.saveError.set(null);
        try {
            const result = await this.variantsService.createVariant({
                RealmId: this.realmId,
                GameId: this.gameId,
                GameVariantTemplateId: t.GameVariantTemplateId,
                IsActive: isActive,
                DisplayInfo: displayInfo,
            });
            if (result.kind === 'ok') {
                this.created.emit();
                this.close();
            } else {
                this.saveError.set(this.formatError(result.reason));
            }
        } finally {
            this.saving.set(false);
        }
    }

    private formatError(reason: string): string {
        switch (reason) {
            case 'GameNotFound':
                return 'Game not found.';
            default:
                return 'Failed to create variant. Please try again.';
        }
    }
}
